import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/Common";
import { MyFiles } from "@/components/DocumentCenter";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

const FIELDS = [
  ["legal_name", "Legal business name"], ["trading_name", "Trading name"], ["registration_number", "ABN / company registration no."],
  ["industry", "Industry"], ["website", "Website"], ["company_size", "Team size"], ["year_founded", "Year founded"],
  ["address", "Business address"], ["country", "Country"], ["contact_person", "Hiring contact name"], ["contact_role", "Contact role"],
  ["contact_phone", "Contact phone"], ["working_hours", "Working hours / timezone"],
];
const AREAS = [["about", "About the business"], ["hiring_needs", "What roles you hire for"], ["benefits", "Benefits & perks for workers"]];

export default function BusinessProfile() {
  const { refresh } = useAuth();
  const [f, setF] = useState({});
  const [meta, setMeta] = useState({ complete: false, required: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/business/profile").then((r) => { setF(r.data.profile); setMeta(r.data); }); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/business/profile", f);
      setMeta(data);
      await refresh();
      toast.success("Business profile saved");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const req = (k) => meta.required.includes(k);

  return (
    <div>
      <PageHeader eyebrow="Your company" title="Business profile" desc="Candidates see this profile when you contact them or send an offer.">
        <Button data-testid="business-profile-save" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save profile"}</Button>
      </PageHeader>
      <div data-testid="profile-completeness" className={`mb-8 flex items-center gap-3 rounded-md border p-4 text-sm ${meta.complete ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
        {meta.complete ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
        {meta.complete ? "Profile complete. You can send offers to candidates." : "Fill in the fields marked * to unlock sending offers."}
      </div>
      <div className="grid gap-4 rounded-md border border-stone-200 bg-white p-6 sm:grid-cols-2 sm:p-8 lg:grid-cols-3">
        {FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label>{l}{req(k) && " *"}</Label><Input data-testid={`bp-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
        ))}
        {AREAS.map(([k, l]) => (
          <div key={k} className="space-y-1.5 sm:col-span-2 lg:col-span-3"><Label>{l}</Label><Textarea data-testid={`bp-${k}`} rows={3} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
        ))}
      </div>
      <h2 className="mb-2 mt-14 font-display text-2xl font-bold text-stone-900">Business licenses & files</h2>
      <p className="mb-6 text-sm text-stone-600">Upload your registration, licenses, insurance or contracts. Mark a file as shared to let candidates you've contacted view it.</p>
      <MyFiles ownerType="business" />
    </div>
  );
}
